/**
 * curtain-autofill.js
 * 从 localStorage 读取窗帘测量数据，自动填充 Easify Product Options 选项
 * 更新日期: 2026-07
 */

// ─── 映射表 ───────────────────────────────────────────────────────────────────

const HEADER_STYLE_MAP = {
  "grommet":            "Grommet Top",
  "back_tab":           "Back Tab",
  "double_pinch_pleat": "Double Pinch Pleat",
  "flat_panel":         "Flat Panel",
  "pencil_pleat":       "Pencil Pleat",
  "ripple_fold":        "Ripple Fold",
  "rod_pocket":         "Rod Pocket",
  "tab_top":            "Tab Top",
  "triple_pinch_pleat": "Triple Pinch Pleat",
};

const PANEL_TYPE_MAP = {
  "single": "Single Panel",
  "split":  "Pair (2 Panels)",
};

// 待补充：fullness / finishedLength 映射表
// const FULLNESS_MAP = { ... };
// const FINISHED_LENGTH_MAP = { ... };

// ─── 数据读取 & 计算 ──────────────────────────────────────────────────────────

function loadCurtainMeasurement() {
  const raw = localStorage.getItem("curtarra.measurement.state.v1");
  if (!raw) {
    console.warn("[curtain-autofill] localStorage 无数据");
    return null;
  }

  let entry;
  try {
    entry = JSON.parse(raw);
  } catch (e) {
    console.error("[curtain-autofill] JSON 解析失败", e);
    return null;
  }

  let width, height;
  if (entry.hasRod) {
    width  = entry.flowA.rodWidth;
    height = entry.flowA.topFrameToFloor;
  } else {
    const { windowWidth, extLeft, extRight, rodAboveFrame, windowHeight } = entry.flowB;
    width  = windowWidth + extLeft + extRight;
    height = rodAboveFrame + windowHeight;
  }

  const isSingle     = entry.panelType === "single";
  const displayWidth = isSingle ? width : width / 2;
  const quantity     = isSingle ? 1 : 2;

  return {
    displayWidth,
    height,
    quantity,
    headerStyle: HEADER_STYLE_MAP[entry.headerStyle] ?? entry.headerStyle,
    panelType:   PANEL_TYPE_MAP[entry.panelType]     ?? entry.panelType,
    // 待补充
    // fullness:       FULLNESS_MAP[entry.fullness]              ?? entry.fullness,
    // finishedLength: FINISHED_LENGTH_MAP[entry.finishedLength] ?? entry.finishedLength,
  };
}

// ─── Easify 图片选项点击 ───────────────────────────────────────────────────────

function setEasifyImageOption(groupDataOriginalText, altValue) {
  const groupLabel = Array.from(
    document.querySelectorAll("label.po-label-label, label[data-original-text]")
  ).find(el =>
    (el.dataset.originalText || el.textContent.trim()).replace(/:.*/, "").trim() === groupDataOriginalText
  );

  if (!groupLabel) {
    console.warn(`[curtain-autofill] ⚠️ 未找到选项组: ${groupDataOriginalText}`);
    return;
  }

  const container = groupLabel.closest(
    "[class*='po-field-root'], [class*='po-group-root'], [class*='po-field']"
  );
  if (!container) {
    console.warn(`[curtain-autofill] ⚠️ 未找到容器: ${groupDataOriginalText}`);
    return;
  }

  const images = container.querySelectorAll("img[alt]");
  for (const img of images) {
    if (img.alt.trim() === altValue) {
      const clickTarget = img.closest("label") || img.closest("[class*='swatch']") || img.parentElement;
      clickTarget.click();
      console.log(`[curtain-autofill] ✅ ${groupDataOriginalText} = ${altValue}`);
      return;
    }
  }

  console.warn(`[curtain-autofill] ⚠️ 未找到图片 alt="${altValue}" 在组: ${groupDataOriginalText}`);
}


// ─── Easify 单选按钮选项（Panel 用）──────────────────────────────────────────

function setEasifyRadioOption(groupDataOriginalText, value) {
  const groupLabel = Array.from(
    document.querySelectorAll("label.po-label-label, label[data-original-text]")
  ).find(el =>
    (el.dataset.originalText || el.textContent.trim()).replace(/:.*/, "").trim() === groupDataOriginalText
  );

  if (!groupLabel) {
    console.warn(`[curtain-autofill] ⚠️ 未找到选项组: ${groupDataOriginalText}`);
    return;
  }

  const container = groupLabel.closest(
    "[class*='po-field-root'], [class*='po-group-root'], [class*='po-field']"
  );
  if (!container) {
    console.warn(`[curtain-autofill] ⚠️ 未找到容器: ${groupDataOriginalText}`);
    return;
  }

  const buttons = container.querySelectorAll("button, label, [class*='po-btn'], [class*='po-option']");
  for (const btn of buttons) {
    if (btn.textContent.trim() === value) {
      btn.click();
      console.log(`[curtain-autofill] ✅ ${groupDataOriginalText} = ${value}`);
      return;
    }
  }

  console.warn(`[curtain-autofill] ⚠️ 未找到按钮: ${groupDataOriginalText} = ${value}`);
}


// ─── Easify 尺寸输入框 ────────────────────────────────────────────────────────

function setEasifyDimensionInput(axisLabel, value) {
  const labelEls = document.querySelectorAll("label, span, [class*='label']");

  for (const labelEl of labelEls) {
    if (labelEl.textContent.trim().toLowerCase() !== axisLabel.toLowerCase()) continue;

    const container = labelEl.closest("div, td, tr, [class*='dimension'], [class*='po-']");
    const input = container?.querySelector("input[type='number'], input[type='text']");

    if (input) {
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype, "value"
      ).set;
      nativeSetter.call(input, value);
      input.dispatchEvent(new Event("input",  { bubbles: true }));
      input.dispatchEvent(new Event("change", { bubbles: true }));
      console.log(`[curtain-autofill] ✅ ${axisLabel} = ${value}`);
      return;
    }
  }

  console.warn(`[curtain-autofill] ⚠️ 未找到输入框: ${axisLabel}`);
}

// ─── 数量 ─────────────────────────────────────────────────────────────────────

function setQuantity(qty) {
  const input = document.querySelector(
    'input[name="quantity"].quantity-selector_input, input[name="quantity"][class*="quantity-selector"]'
  );
  if (!input) {
    console.warn("[curtain-autofill] ⚠️ 未找到数量输入框");
    return;
  }

  const nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype, "value"
  ).set;
  nativeSetter.call(input, qty);
  input.dispatchEvent(new Event("input",  { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
  input.form?.dispatchEvent(new Event("change", { bubbles: true }));

  console.log(`[curtain-autofill] ✅ 数量 = ${qty}`);
}

function expandFilledGroups() {
  const clickableLabels = document.querySelectorAll("[class*='po-label-clickable']");
  clickableLabels.forEach(label => {
    const labelEl = label.querySelector("label[data-original-text], [class*='po-label-label']");
    const text = labelEl ? labelEl.textContent.trim() : label.textContent.trim();
    
    // 只展开有值的组（label 文字包含 ": "）
    if (text.includes(": ")) {
      label.click();
      console.log("[curtain-autofill] 📂 展开组:", text.slice(0, 50));
    } else {
      console.log("[curtain-autofill] ⏭️ 跳过空组:", text.slice(0, 30));
    }
  });
}




// ─── 主入口 ───────────────────────────────────────────────────────────────────

function autofillFromMeasurement() {
  const data = loadCurtainMeasurement();
  if (!data) return;

  console.log("[curtain-autofill] 📐 填充数据:", data);

  setTimeout(() => {
    setEasifyImageOption("Header", data.headerStyle);
    setEasifyRadioOption("Panel",  data.panelType);
    setEasifyDimensionInput("Width",  data.displayWidth);
    setEasifyDimensionInput("Length", data.height);
    setQuantity(data.quantity);

    setTimeout(() => {
      expandFilledGroups();
    }, 400);

  }, 1200);
}


if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autofillFromMeasurement);
} else {
  autofillFromMeasurement();
}
